import type { LogEntry } from "@skaile/workspaces/types";
import pc from "picocolors";

export interface FormatOptions {
  json?: boolean;
  nextCursor?: string;
}

/**
 * Render log entries to stdout. `--json` dumps a single JSON array; otherwise
 * each entry is one timestamp + level + source + message line, with `data` and
 * `error.stack` indented underneath when present.
 */
export function formatEntries(entries: LogEntry[], opts: FormatOptions = {}): string {
  if (opts.json) {
    return JSON.stringify(entries, null, 2);
  }
  const lines: string[] = [];
  for (const e of entries) {
    lines.push(...renderEntryLines(e));
  }
  if (opts.nextCursor) {
    lines.push(pc.dim(`... more available. Use --cursor ${opts.nextCursor} to page back.`));
  }
  return lines.join("\n");
}

/**
 * Render a single entry for streaming output. In `--json` mode this emits one
 * compact JSON object per line (NDJSON), so consumers can stream-parse the
 * tail. In pretty mode it produces the same multi-line shape as
 * `formatEntries` but for one entry only.
 */
export function formatSingleEntry(entry: LogEntry, opts: { json?: boolean } = {}): string {
  if (opts.json) {
    return JSON.stringify(entry);
  }
  return renderEntryLines(entry).join("\n");
}

function renderEntryLines(e: LogEntry): string[] {
  const lines: string[] = [formatLine(e)];
  if (e.data && Object.keys(e.data).length > 0) {
    lines.push(...formatData(e.data));
  }
  if (e.error) {
    lines.push(...formatError(e.error));
  }
  return lines;
}

function formatLine(e: LogEntry): string {
  const ts = pc.dim(`[${formatTime(e.timestamp)}]`);
  // colorLevel already pads each label to 5 visible chars; padEnd on the
  // ANSI-wrapped string is a no-op (length includes escape sequences).
  const lvl = colorLevel(e.level);
  const src = pc.cyan(`[${formatSource(e.source)}]`);
  return `${ts} ${lvl} ${src} ${e.message}`;
}

function formatTime(iso: string): string {
  // HH:MM:SS.mmm in UTC (matches the spec example).
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  const ms = String(d.getUTCMilliseconds()).padStart(3, "0");
  return `${hh}:${mm}:${ss}.${ms}`;
}

function formatSource(src: LogEntry["source"]): string {
  if (src.instance) return `${src.kind}:${src.subkind}:${src.instance}`;
  return `${src.kind}:${src.subkind}`;
}

function colorLevel(level: LogEntry["level"]): string {
  switch (level) {
    case "error":
      return pc.red("ERROR");
    case "warn":
      return pc.yellow("WARN ");
    case "info":
      return pc.green("INFO ");
    case "debug":
      return pc.dim("DEBUG");
    default:
      return String(level).toUpperCase();
  }
}

function formatData(data: Record<string, unknown>): string[] {
  const oneLine = JSON.stringify(data);
  if (oneLine.length <= 120) {
    return [`  ${pc.dim("> data:")} ${oneLine}`];
  }
  const pretty = JSON.stringify(data, null, 2);
  return [`  ${pc.dim("> data:")}`, ...pretty.split("\n").map((l) => `    ${l}`)];
}

function formatError(err: NonNullable<LogEntry["error"]>): string[] {
  const head = `  ${pc.dim("> error:")} ${pc.red(err.name)}: ${err.message}`;
  if (!err.stack) return [head];
  const stackLines = err.stack
    .split("\n")
    .filter((l) => l.trim().length > 0)
    .map((l) => `    ${pc.dim(l)}`);
  return [head, ...stackLines];
}
